/**
 * Push campaign attribution (`utm_*`) forwarded in the purchase creation payload.
 *
 * The keys are read ONCE, at bundle start (see `initializers.js`), and kept in module state
 * until the purchase is created. Trip selection does carry the query string forward, so it is
 * not that path that needs the snapshot: `ProvidersContainer` merges `location.search` into the
 * URL it pushes. A new search does not — `SearchFormContainer` builds its query string from
 * scratch — so reading `window.location.search` at purchase time would drop the campaign for
 * every visitor who re-searches before buying, with no error, no log and no failing request.
 *
 * The key set matches the platform-purchase allow-list (`Api::V2::PurchaseParams`) introduced
 * by the backend campaign attribution change; any key outside it is discarded server-side
 * without notice.
 */

/**
 * Campaign keys forwarded to platform-purchase, in the order the backend declares them.
 * @type {string[]}
 */
export const CAMPAIGN_ATTRIBUTION_KEYS = [
  'utm_campaign',
  'utm_source',
  'utm_medium',
  'utm_id',
  'utm_content',
];

let campaignAttribution = {};

/**
 * Reads the campaign keys present in a query string.
 * Values are passed through verbatim: real campaigns carry brackets, emoji and unexpanded
 * placeholders such as `{campaignname}`, so a format filter here would drop paid traffic.
 * `URLSearchParams` is used rather than `utils/queryString/parseQueryString` because the latter
 * does not percent-decode, and would forward `%7Bcampaignname%7D` instead of `{campaignname}`.
 * @param {string} search - Query string, with or without the leading `?`.
 * @returns {Object} Campaign keys that carry a non-empty value, in snake_case.
 */
function readCampaignAttribution(search) {
  const params = new URLSearchParams(search);

  return CAMPAIGN_ATTRIBUTION_KEYS.reduce((attribution, key) => {
    const value = params.get(key);

    return value ? { ...attribution, [key]: value } : attribution;
  }, {});
}

/**
 * Captures the campaign attribution from the landing URL. Called once, at bundle start.
 * @param {string} [search] - Query string to read; defaults to the current location's.
 * @returns {Object} The captured campaign keys.
 */
export function captureCampaignAttribution(search) {
  const querystring = search ?? (typeof window === 'undefined' ? '' : window.location.search);
  campaignAttribution = readCampaignAttribution(querystring);

  return campaignAttribution;
}

/**
 * Returns the campaign attribution captured at bundle start.
 * @returns {Object} Captured campaign keys, or an empty object when nothing was captured.
 */
export function getCampaignAttribution() {
  return { ...campaignAttribution };
}
